import { useEffect, useState } from 'react';
import { useI18n } from '../i18n/i18nContext';
import { axiosClient } from '../services/api';

export interface ContentPageData {
  slug: string;
  titles: string | { [key: string]: string };
  contents: string | { [key: string]: string };
}

interface ContentPageRendererProps {
  slug: string;
  className?: string;
  preloadedContent?: ContentPageData | null;
}

const ContentPageRenderer = ({ slug, className, preloadedContent }: ContentPageRendererProps) => {
  const { language } = useI18n();
  const [page, setPage] = useState<ContentPageData | null>(preloadedContent || null);
  const [loading, setLoading] = useState(!preloadedContent);

  useEffect(() => {
    if (preloadedContent !== undefined) {
      setPage(preloadedContent);
      setLoading(false);
      return;
    }

    const fetchContent = async () => {
      try {
        setLoading(true);
        const response = await axiosClient.get<ContentPageData>(`/api/content-pages/${slug}`);
        const data = response.data;
        
        const parsedData: ContentPageData = {
          ...data,
          titles: typeof data.titles === 'string' ? JSON.parse(data.titles) : data.titles,
          contents: typeof data.contents === 'string' ? JSON.parse(data.contents) : data.contents,
        };
        
        setPage(parsedData);
      } catch (err) {
        console.error(`Failed to fetch content for slug ${slug}:`, err);
        setPage(null);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [slug, preloadedContent]);

  const parsedPage = page ? {
    ...page,
    titles: typeof page.titles === 'string' ? JSON.parse(page.titles) : page.titles,
    contents: typeof page.contents === 'string' ? JSON.parse(page.contents) : page.contents,
  } : null;

  const contentsObj = parsedPage?.contents;
  const localizedContent = contentsObj?.[language as keyof typeof contentsObj] || contentsObj?.['en'];

  if (loading || !localizedContent) {
    return null;
  }

  return (
    <div className={`prose max-w-none ${className || ''}`} dangerouslySetInnerHTML={{ __html: localizedContent }} />
  );
};

export default ContentPageRenderer;
